import {
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { ArrowUpDown, ChevronDown, ExternalLink } from "lucide-react";
import {
  formatTypographyFamilySummary,
  hexColorDistance,
  parseTypographyStyleClaim,
} from "@runcheetah/shared";

import { Button } from "@runcheetah/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@runcheetah/ui";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/date-format";
import { formatSnakeLabel } from "@/lib/labels";
import { trpc } from "@/lib/trpc";

import { formatFactSourceDisplay } from "./fact-source";
import type { BrandFact } from "./types";

type Fact = BrandFact;

const formatDate = formatDateTime;

function formatCategory(fieldPath: string) {
  return formatSnakeLabel(fieldPath.split(".")[0] ?? fieldPath);
}

function formatFieldName(fieldPath: string) {
  const parts = fieldPath.split(".");
  return formatSnakeLabel(parts[parts.length - 1] ?? fieldPath);
}

const PALETTE_FIELD_PATHS = new Set([
  "visualIdentitySnapshot.colorPalette",
  "visualIdentitySnapshot.secondaryColorPalette",
]);
const PALETTE_DUPLICATE_DISTANCE = 24;

const parsePaletteColors = (value: Fact["valueJson"]): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

const dedupeSecondaryPalette = (secondary: string[], primary: string[]): string[] =>
  secondary.filter(
    (secondaryColor) =>
      !primary.some(
        (primaryColor) =>
          secondaryColor.toLowerCase() === primaryColor.toLowerCase() ||
          hexColorDistance(secondaryColor, primaryColor) <= PALETTE_DUPLICATE_DISTANCE,
      ),
  );

function PaletteSwatches({ colors }: { colors: string[] }) {
  return (
    <div className="flex min-w-[120px] flex-wrap gap-1.5">
      {colors.map((color, i) => (
        <div
          key={`${color}-${i}`}
          className="size-5 rounded-full shadow-sm ring-1 ring-border"
          style={{ backgroundColor: color }}
          title={color}
        />
      ))}
    </div>
  );
}

function normalizePaletteFacts(facts: Fact[]): Fact[] {
  const primaryPalette =
    facts.find((fact) => fact.fieldPath === "visualIdentitySnapshot.colorPalette")?.valueJson ?? [];
  const primaryColors = parsePaletteColors(primaryPalette);

  return facts.map((fact) => {
    if (fact.fieldPath !== "visualIdentitySnapshot.secondaryColorPalette") {
      return fact;
    }

    return {
      ...fact,
      valueJson: dedupeSecondaryPalette(parsePaletteColors(fact.valueJson), primaryColors),
    };
  });
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  let colorClass = "bg-green-50 text-green-700 border-green-200";
  if (confidence < 0.5) colorClass = "bg-red-50 text-red-700 border-red-200";
  else if (confidence < 0.8) colorClass = "bg-yellow-50 text-yellow-700 border-yellow-200";

  return (
    <div
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 font-mono text-xs font-semibold ${colorClass}`}
    >
      {confidence.toFixed(2)}
    </div>
  );
}

function SortButton({
  label,
  onClick,
  sorted,
}: {
  label: string;
  onClick: () => void;
  sorted: false | "asc" | "desc";
}) {
  return (
    <Button variant="ghost" size="sm" className="-ml-3 h-8" onClick={onClick}>
      {label}
      <ArrowUpDown
        className={`ml-2 size-3.5 transition-opacity ${sorted ? "opacity-100" : "opacity-40"}`}
      />
    </Button>
  );
}

const columns: ColumnDef<Fact>[] = [
  {
    id: "category",
    accessorFn: (row) => formatCategory(row.fieldPath),
    header: ({ column }) => (
      <SortButton
        label="Category"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        sorted={column.getIsSorted()}
      />
    ),
    cell: ({ getValue }) => (
      <span className="inline-flex items-center whitespace-nowrap rounded-md bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground">
        {getValue<string>()}
      </span>
    ),
    filterFn: "includesString",
  },
  {
    id: "field",
    accessorFn: (row) => formatFieldName(row.fieldPath),
    header: ({ column }) => (
      <SortButton
        label="Field"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        sorted={column.getIsSorted()}
      />
    ),
    cell: ({ getValue }) => (
      <span className="whitespace-nowrap text-sm font-medium">{getValue<string>()}</span>
    ),
  },
  {
    accessorKey: "confidence",
    header: ({ column }) => (
      <SortButton
        label="Score"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        sorted={column.getIsSorted()}
      />
    ),
    cell: ({ row }) => <ConfidenceBadge confidence={row.original.confidence} />,
  },
  {
    id: "value",
    accessorFn: (row) =>
      typeof row.valueJson === "string" ? row.valueJson : JSON.stringify(row.valueJson),
    header: "Value",
    cell: ({ row, getValue }) => {
      const val = getValue<string>();

      if (PALETTE_FIELD_PATHS.has(row.original.fieldPath)) {
        const colors = parsePaletteColors(row.original.valueJson);
        if (colors.length > 0) {
          return <PaletteSwatches colors={colors} />;
        }
      }

      if (row.original.fieldPath === "visualIdentitySnapshot.typographyStyle") {
        const claim = parseTypographyStyleClaim(row.original.valueJson);
        const lines = claim
          ? claim.families.length > 0
            ? claim.families.map((family) => formatTypographyFamilySummary(family))
            : claim.summary
              ? claim.summary.split(" | ")
              : []
          : val
            ? val.split(" | ")
            : [];

        if (lines.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {lines.map((fontStr, i) => (
                <span
                  key={i}
                  className="inline-flex items-center rounded-sm bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                >
                  {fontStr}
                </span>
              ))}
            </div>
          );
        }
      }

      const isUrl = val.startsWith("http://") || val.startsWith("https://");

      if (isUrl) {
        return (
          <a
            href={val}
            target="_blank"
            rel="noopener noreferrer"
            className="flex max-w-[220px] items-center gap-1 truncate font-mono text-sm text-blue-600 hover:underline"
            title={val}
          >
            <span className="truncate">{val}</span>
            <ExternalLink className="size-3 shrink-0" />
          </a>
        );
      }

      return (
        <div className="max-w-[220px] truncate font-mono text-sm" title={val}>
          {val}
        </div>
      );
    },
  },
  {
    accessorKey: "rationale",
    header: "Reasoning",
    cell: ({ row }) => (
      <div className="flex max-w-[360px] items-start gap-2">
        <span className="line-clamp-3 text-sm text-muted-foreground" title={row.original.rationale}>
          {row.original.rationale}
        </span>
      </div>
    ),
  },
  {
    id: "source",
    accessorFn: (row) => formatFactSourceDisplay(row.sourceUrl, row.pageUrl).label,
    header: "Source",
    cell: ({ row }) => {
      const source = formatFactSourceDisplay(row.original.sourceUrl, row.original.pageUrl);

      return (
        <a
          href={row.original.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex max-w-[260px] items-center gap-1 whitespace-nowrap font-mono text-xs text-muted-foreground hover:text-foreground"
          title={row.original.sourceUrl}
        >
          <span className="truncate">{source.label}</span>
          {source.isExternal ? (
            <span className="rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
              external
            </span>
          ) : null}
          <ExternalLink className="size-3 shrink-0" />
        </a>
      );
    },
  },
  {
    id: "page",
    accessorFn: (row) => row.pageType ?? "",
    header: "Page Type",
    cell: ({ row }) => (
      <span className="text-xs capitalize text-muted-foreground">
        {row.original.pageType ?? "—"}
      </span>
    ),
  },
  {
    accessorKey: "engineKind",
    header: "Engine",
    cell: ({ row }) => (
      <span className="text-xs capitalize text-muted-foreground">{row.original.engineKind}</span>
    ),
  },
  {
    accessorKey: "model",
    header: "Model",
    cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">{row.original.model ?? "—"}</span>
    ),
  },
  {
    accessorKey: "createdAt",
    header: ({ column }) => (
      <SortButton
        label="Extracted At"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        sorted={column.getIsSorted()}
      />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-xs text-muted-foreground">
        {formatDate(row.original.createdAt)}
      </span>
    ),
  },
];

function FactsDataTable({ data }: { data: Fact[] }) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "confidence", desc: true }]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    engineKind: false,
    model: false,
  });

  const table = useReactTable({
    data,
    columns,
    state: { sorting, columnFilters, globalFilter, columnVisibility },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 15 } },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Input
          placeholder="Filter facts…"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="h-8 max-w-xs text-sm"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="ml-auto h-8">
              Columns <ChevronDown className="ml-2 size-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((col) => col.getCanHide())
              .map((col) => (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  className="capitalize"
                  checked={col.getIsVisible()}
                  onCheckedChange={(v) => col.toggleVisibility(v)}
                >
                  {formatSnakeLabel(col.id)}
                </DropdownMenuCheckboxItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="hover:bg-transparent">
                {hg.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} className="align-top">
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  No matching facts.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {table.getFilteredRowModel().rows.length} fact
          {table.getFilteredRowModel().rows.length !== 1 ? "s" : ""} total
        </span>
        <div className="flex items-center gap-2">
          <span>
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

export function FactsTable({ brandId }: { brandId: string }) {
  const { data: facts, isPending, error } = trpc.admin.brands.facts.useQuery({ brandId });
  const normalizedFacts = useMemo(() => normalizePaletteFacts(facts ?? []), [facts]);

  if (isPending) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Extracted Facts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-8 w-xs" />
          <Skeleton className="h-64 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Extracted Facts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-24 items-center justify-center rounded-md border text-sm text-destructive">
            Failed to load facts
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium">Extracted Facts</CardTitle>
      </CardHeader>
      <CardContent>
        {normalizedFacts.length > 0 ? (
          <FactsDataTable data={normalizedFacts} />
        ) : (
          <div className="flex h-24 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground">
            No extracted facts found for this brand yet.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
